import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  FontManager,
  setFontManager as setGlobalFontManager,
} from '@builder4/shared/fonts/manager';
import { fetchGoogleFonts } from '@builder4/shared/fonts/data';
import type { ProcessedFont } from '@builder4/shared/types/fonts';
import { useAppDispatch } from '@/store/hooks';
import { showToast } from '@/store/notifications/slice';
import { injectGlobal } from '@emotion/css';

const useFontError = () => {
  const [hasShownError, setHasShownError] = useState(false);

  const showError = useCallback(
    (dispatch: ReturnType<typeof useAppDispatch>) => {
      if (!hasShownError) {
        setHasShownError(true);
        dispatch(
          showToast({
            text: 'Unable to load fonts. Please refresh the page.',
            state: 'warning',
            showCloseButton: false,
            autoDismiss: false,
          }),
        );
      }
    },
    [hasShownError],
  );

  const resetError = useCallback(() => {
    setHasShownError(false);
  }, []);

  return { showError, resetError };
};

interface FontContextType {
  fontManager: FontManager | null;
  fonts: ProcessedFont[];
  isLoading: boolean;
  hasError: boolean;
}

const FontContext = createContext<FontContextType | null>(null);

export const useFonts = () => {
  const context = useContext(FontContext);
  if (!context) {
    throw new Error('useFonts must be used within a FontProvider');
  }
  return context;
};

export const useAppReady = () => {
  const { isLoading, hasError } = useFonts();
  return {
    isReady: !isLoading && !hasError,
    isLoading,
    hasError,
  };
};

interface FontProviderProps {
  children: React.ReactNode;
}

export const FontProvider: React.FC<FontProviderProps> = ({ children }) => {
  const [fontManager, setFontManager] = useState<FontManager | null>(null);
  const [fonts, setFonts] = useState<ProcessedFont[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const dispatch = useAppDispatch();
  const { showError, resetError } = useFontError();

  useEffect(() => {
    let cancelled = false;

    const initializeFonts = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const apiKey = import.meta.env.VITE_GOOGLE_FONTS_API_KEY;
        if (!apiKey) {
          throw new Error('VITE_GOOGLE_FONTS_API_KEY environment variable is not set');
        }

        const liveFontData = await fetchGoogleFonts(apiKey);

        if (cancelled) return;

        if (!liveFontData || Object.keys(liveFontData).length === 0) {
          throw new Error('Google Fonts API returned empty or invalid data');
        }

        const manager = new FontManager(liveFontData);

        if (!cancelled) {
          setGlobalFontManager(manager);
          setFontManager(manager);
        } else {
          return;
        }

        const fontFamilies = Object.values(liveFontData).map((font) => ({
          family: font.family,
          variants: font.variants.map((v) => v.variant),
        }));

        if (!cancelled) {
          setFonts(fontFamilies);
          setIsLoading(false);
          resetError();

          const globalFontCss = fontFamilies
            .map((font) => {
              const fontData = manager.getFontData(font.family);
              if (!fontData) return '';

              return fontData.variants
                .map((variant) => {
                  return manager.getFontFaceCss(font.family, variant.weight, variant.style);
                })
                .join('\n');
            })
            .join('\n');

          if (globalFontCss) {
            injectGlobal(globalFontCss);
          }
        }
      } catch (error) {
        console.error('Failed to load fonts:', error);
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
          showError(dispatch);
        }
      }
    };

    initializeFonts();

    return () => {
      cancelled = true;
    };
  }, []);

  const value: FontContextType = {
    fontManager,
    fonts,
    isLoading,
    hasError,
  };

  return <FontContext.Provider value={value}>{children}</FontContext.Provider>;
};
